// Element Halls: one hall per element (from shared/elements.ts). A trainer at
// Lv 150+ registers a team of up to 5 creatures of the hall's element with no
// species twice; the hall's Hall Keeper deals +25% damage with creatures of
// that element on the hunt and against bosses. The title stays until another
// registered trainer beats the keeper in a challenge: a one-on-one attrition
// duel on the arena's battle (shared/arena.ts battle), at the arena's
// compressed levels, without the keeper's bonus. Pure rules shared by the
// server (server/halls.ts), the client and the tests.
import { ELEMENTS, ELEMENT_IDS, type Element } from './elements.ts';
import { SPECIES } from './species.ts';
import { ARENA, type BattleResult } from './arena.ts';
import type { BoardRow } from './board.ts';

export const HALLS = {
  /** Trainer level to register a team or challenge. */
  unlockLevel: ARENA.unlockLevel,
  teamMax: ARENA.teamMax,
  /** The keeper's damage multiplier with creatures of the hall's element, on the hunt and against bosses. */
  keeperMult: 1.25,
  /** Wait between two challenges of the same hall by the same trainer. Each hall has its own clock. */
  cooldownMs: 24 * 3_600_000,
  /** Registered teams shown per hall, by team power. */
  podium: 50,
} as const;

export const KEEPER_TITLE = 'Hall Keeper';
export const HALL_IDS: readonly Element[] = ELEMENT_IDS;
export const isHall = (x: unknown): x is Element => typeof x === 'string' && (HALL_IDS as readonly string[]).includes(x);
export const hallName = (el: Element) => `${ELEMENTS[el].name} Hall`;

/** A species fits a hall when one of its elements is the hall's (a dual-element species fits both). */
export function fitsHall(species: string, el: Element): boolean {
  return !!SPECIES[species]?.elements.includes(el);
}

/**
 * The keeper's damage multiplier for a creature: ×1.25 when the trainer keeps
 * a hall of one of its elements. A dual-element creature whose trainer keeps
 * both halls still gets ×1.25 once; it never stacks.
 */
export function keeperMult(species: string, halls: readonly string[] | undefined): number {
  if (!halls?.length) return 1;
  const els = SPECIES[species]?.elements ?? [];
  return els.some((e) => halls.includes(e)) ? HALLS.keeperMult : 1;
}

/** Why a team can't be registered at a hall, or null when it can. */
export function registerProblem(team: { species: string }[], el: Element): string | null {
  if (!team.length) return `A hall team needs 1 to ${HALLS.teamMax} creatures.`;
  if (team.length > HALLS.teamMax) return `A hall team has at most ${HALLS.teamMax} creatures.`;
  const off = team.find((c) => !fitsHall(c.species, el));
  if (off) return `${SPECIES[off.species]?.name ?? off.species} is not a ${ELEMENTS[el].name} creature.`;
  const seen = new Set<string>();
  for (const c of team) {
    if (seen.has(c.species)) return `${SPECIES[c.species].name} is on the team twice. Each species can be on a hall team once.`;
    seen.add(c.species);
  }
  return null;
}

/**
 * The creatures that count for a registered team, in its order: those still
 * of the hall's element, and only the first of each species. A member that
 * ascends into a species already on the team stops counting (the earlier
 * member keeps its place).
 */
export function countingTeam<T extends { species: string }>(team: T[], el: Element): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const c of team) {
    if (!fitsHall(c.species, el) || seen.has(c.species)) continue;
    seen.add(c.species);
    out.push(c);
    if (out.length >= HALLS.teamMax) break;
  }
  return out;
}

/** When this trainer may next challenge a hall, given their last challenge there (0 = never). */
export const challengeReadyAt = (lastAt: number) => (lastAt > 0 ? lastAt + HALLS.cooldownMs : 0);

/**
 * Who keeps a hall after a challenge: the challenger (side 0) when they win
 * the duel, else the keeper. The duel itself is shared/arena.ts battle(),
 * which already uses the arena's level compression and no keeper bonus.
 */
export const keeperAfter = (keeper: number, challenger: number, winner: 0 | 1) => (winner === 0 ? challenger : keeper);

/** The registered team that takes a vacant hall: the earliest registration (then the lower account). */
export function firstRegistered<T extends { account: number; at: number }>(rows: T[]): T | null {
  let best: T | null = null;
  for (const r of rows) if (!best || r.at < best.at || (r.at === best.at && r.account < best.account)) best = r;
  return best;
}

// ---------- messages ----------
export interface HallMon { species: string; level: number; arenaLevel: number; aura: boolean }
export interface HallEntry { rank: number; name: string; outfit: string; power: number; team: HallMon[]; keeper?: boolean; me?: boolean }
export interface HallDuelSummary {
  id: number; ts: number; el: Element;
  /** You challenged (else you were the keeper defending). */
  challenged: boolean;
  opp: string; win: boolean;
  /** The hall changed hands. */
  took: boolean;
}
export interface HallView {
  el: Element;
  keeper: { name: string; outfit: string; since: number; power: number; team: HallMon[]; me: boolean } | null;
  podium: HallEntry[];
  /** Your registration here, if any: the creature ids you registered and those that count now. */
  mine: { ids: number[]; counting: number[]; power: number; rank: number; /** Front or back per id (the board); absent: defaultRows. */ rows?: BoardRow[] } | null;
  /** When you may challenge this hall next (0 = now). */
  readyAt: number;
}
export interface HallsDTO {
  unlocked: boolean;
  /** Admin accounts can't register or keep a hall. */
  admin: boolean;
  halls: HallView[];
  /** Your recent challenges and defenses, newest first. */
  duels: HallDuelSummary[];
}
export interface HallDuelMsg { id: number; side: 0 | 1; result: BattleResult; summary: HallDuelSummary }
